import React, { useEffect, useState } from 'react';
import ValidationService from '../../../../../../services/validation';
import GeneralService from '../../../../../../services/general';
import OutcomePaymentService from '../../../../../../services/finance/outcomes/payments';
import { DefaultButton, DetailsList, Panel, PanelType, PrimaryButton, SelectionMode, Spinner, SpinnerSize, Stack, TextField } from '@fluentui/react';
import Label from '../../../../../typography/label';
import Text from '../../../../../typography/text';
import { IOutcomePaymentResourceProps, IOutcomePaymentResourceShortProps } from '../../../../../../props/finance/outcomes/payments';
import PermissionsService from '../../../../../../services/permissions';
import { useStore } from '../../../../../../stores/root';
import ErrorService from '../../../../../../services/general/error';
import { faXmarkCircle } from '@fortawesome/pro-light-svg-icons';
import { IOutcomePaymentInvoiceResourceShortProps } from '../../../../../../props/finance/outcomes/payments/invoice';
import LoadingComponent from '../../../../../feedbacks/loading';
import { IIncomePaymentResourceProps, IIncomePaymentResourceShortProps } from '../../../../../../props/finance/incomes/payments/payment';
import IncomePaymentService from '../../../../../../services/finance/incomes/payments/payment';
import Details from '../../../../../typography/details';
import PaymentMethods from '../../../../../../manifests/paymentMethods';
import IncomePaymentInvoicesList from '../../invoices/general/list';
import moment from 'moment';

interface IIncomePaymentRejectFormProps {
    paymentId: string;
    onDismissed(refresh?: boolean): void;
}

type FormDataProps = {
    reason: string;
}

type FormDataErrorProps = {
    reason?: string;
}

const IncomePaymentRejectForm: React.FC<IIncomePaymentRejectFormProps> = (props: IIncomePaymentRejectFormProps) => {
    const { banner, user } = useStore();
    const [submitting, setSubmitting] = useState<boolean>(false)
    const [data, setData] = React.useState<FormDataProps>({
        reason: "",
    });
    const [payment, setPayment] = React.useState<IIncomePaymentResourceProps | undefined>();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [error, setError] = React.useState<FormDataErrorProps>({});

    const hasPermission = PermissionsService.hasPermission(['incomes.payments.reject'], user.permissions);

    const _onSubmit = async () => {
        setSubmitting(true)
        try {
            const fd = new FormData();
            fd.append('reason', data.reason);

            await IncomePaymentService.reject(props.paymentId, fd);
            props.onDismissed(true)
        } catch (error) {
            setSubmitting(false)
        }
    }

    useEffect(() => {
        _onRetrievePayment();
    }, []);

    const _onRetrievePayment = async () => {
        try {
            setLoaded(false);

            const _payment = await IncomePaymentService.get(props.paymentId);
            setPayment(_payment);

            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'get_payment_error',
                text: 'Failed to retrieve payments. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    }

    return <Panel
        headerText={'Reject Payment'}
        isOpen={true}
        type={PanelType.medium}
        onDismiss={() => props.onDismissed(false)}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack horizontal tokens={{ childrenGap: 10 }}>
                {
                    !submitting && hasPermission ? (
                        <>
                            <PrimaryButton disabled={data.reason.trim() === '' || error.reason !== undefined} text={"Reject"} onClick={_onSubmit} />
                            <DefaultButton text={"Cancel"} onClick={() => { props.onDismissed(false) }} />
                        </>
                    ) : null
                }
                {submitting ? <Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Recording payment ..."} /> : null}
            </Stack>;
        }}
    >
        {hasPermission ? <Stack tokens={{ childrenGap: 15 }}>
            {!loaded ? <LoadingComponent label={'Retrieving form ...'} labelPosition={'right'} spinnerPosition={'baseline'} /> : null}
            {loaded && payment ? <>
                <Text>Are you sure to REJECT payment with details below?</Text>
                <Details title={'Payment date'} text={moment(payment.paymentDate).format("DD/MM/YYYY")} />
                <Details title={'Method'} text={PaymentMethods.find((pm) => pm.key === payment.method)?.text || 'Cash'} />
                {payment.transferTo ? <Details title={'Transfer to'} text={`${payment.transferTo.accountNumber} a/n ${payment.transferTo.accountName} (${payment.transferTo.bank.name})`} /> : null}
                <Details title={'Amount'} text={`Rp. ${GeneralService.getNumberWithSeparator(Number(payment.amount))}`} />
                <Stack className={'divider'} />
                <Label size={'small'}>Related invoices</Label>
                <IncomePaymentInvoicesList payment={payment}
                    paymentInvoices={payment.invoices}
                    columns={['invoice', 'amount']}
                    hideCommandBar />
                <Stack className={'divider'} />
                <Stack>
                    <TextField
                        label={"Reason"}
                        value={data.reason}
                        required
                        multiline
                        rows={3}
                        resizable={false}
                        autoAdjustHeight
                        onChange={(evt, value) => {
                            data.reason = value || "";

                            const validation = ValidationService.combination(value, ['limit'], { maxChars: 100 });
                            error.reason = validation.message;

                            setData({ ...data });
                            setError({ ...error });
                        }}
                        errorMessage={error.reason}
                        disabled={submitting} />
                </Stack>
            </> : null}
        </Stack> : null}
    </Panel>
};

export default IncomePaymentRejectForm;
